import { AsyncPipe } from '@angular/common';
import { AfterViewInit, Component, inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Data } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SliderComponent } from '../../snippets/slider/slider.component';
import { SliderViewComponent } from '../../snippets/slider-view/slider-view.component';
import { SkillTestComponent } from '../../snippets/skill-test/skill-test.component';
import { SpinnerComponent } from '@catch/general-ui';
import { TooltipDirective } from '@catch/directives';
import { LanguageService } from '@catch/services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, map, take } from 'rxjs';
import { collapseZoom } from '../../../../assets/animations/animations';
import { emailRegex, nameRegex } from '../../../../assets/regex';
import { JobboardAdapter } from '../../../adapters/jobboard.adapter';
import { StandaloneIsaCheckbox } from '../../../enums/standalone-isa-checkbox.enum';
import { ColorService } from '../../../services/color.service';
import { FontawesomeService } from '../../../services/fontawesome.service';

@UntilDestroy()
@Component({
  selector: 'app-standalone-isa',
  templateUrl: './standalone-isa.component.html',
  styleUrls: ['./standalone-isa.component.scss'],
  animations: [collapseZoom],
  imports: [
    AsyncPipe,
    TranslocoPipe,
    FormsModule,
    ReactiveFormsModule,
    SliderComponent,
    SliderViewComponent,
    SkillTestComponent,
    FaIconComponent,
    SpinnerComponent,
    TooltipDirective,
  ],
})
export class StandaloneIsaComponent implements AfterViewInit {
  private readonly fb = inject(FormBuilder);
  private readonly colorService = inject(ColorService);
  private readonly jobboardAdapter = inject(JobboardAdapter);
  public readonly fontawesomeService = inject(FontawesomeService);
  private readonly route = inject(ActivatedRoute);
  public readonly languageService = inject(LanguageService);

  @ViewChild('slider') slider: SliderComponent;
  isaForm: FormGroup;
  contactForm: FormGroup;
  privacyAccepted: boolean = false;
  allowRetargeting: boolean = false;
  submitAttempt: boolean = false;
  submitting: boolean = false;
  isaLinkInfo$: Observable<{
    eventName: string;
    checkboxes: { id: StandaloneIsaCheckbox; on: boolean }[];
  }>;
  isaLinkId$: Observable<string>;
  privacyCheckboxExpanded = false;
  retargetingCheckboxExpanded = false;
  privacyCheckboxEnabled = true;
  retargetingCheckboxEnabled = true;

  constructor() {
    this.isaLinkInfo$ = this.route.data.pipe(
      map((data: Data) => data['isaLink'])
    );
    this.isaLinkId$ = this.route.params.pipe(map((params) => params['linkId']));
    this.isaForm = this.fb.group({
      isa: [null, [Validators.required]],
    });
    this.contactForm = this.fb.group({
      firstName: [null, [Validators.required, Validators.pattern(nameRegex)]],
      lastName: [null, [Validators.required, Validators.pattern(nameRegex)]],
      email: [
        null,
        [Validators.required, Validators.email, Validators.pattern(emailRegex)],
      ],
    });
    this.colorService.setDefaultDesign();
  }

  ngAfterViewInit(): void {
    this.isaLinkId$.pipe(untilDestroyed(this)).subscribe((id: string) => {
      if (!id || id === '') return;
      const alreadySentFromThisDevice = localStorage.getItem(id + '-click');
      if (alreadySentFromThisDevice !== null) return;
      this.jobboardAdapter
        .countStandaloneISAClick(id)
        .pipe(take(1))
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          localStorage.setItem(id + '-click', 'true');
        });
    });

    this.isaLinkInfo$
      .pipe(untilDestroyed(this))
      .pipe(take(1))
      .subscribe(
        (info: {
          eventName: string;
          checkboxes: { id: StandaloneIsaCheckbox; on: boolean }[];
        }) => {
          for (const checkbox of info.checkboxes) {
            switch (checkbox.id) {
              case StandaloneIsaCheckbox.PRIVACY:
                this.privacyCheckboxEnabled = checkbox.on;
                break;
              case StandaloneIsaCheckbox.RETARGETING:
                this.retargetingCheckboxEnabled = checkbox.on;
                break;
            }
          }
        }
      );
  }

  submit() {
    if (this.submitting) return;

    this.submitAttempt = true;

    if (this.isaForm.invalid) {
      this.slider.setActiveIndex(1);
      return;
    }
    if (this.contactForm.invalid) return;

    this.submitting = true;
    this.isaLinkId$
      .pipe(take(1))
      .pipe(untilDestroyed(this))
      .subscribe((id: string) => {
        this.jobboardAdapter
          .generateStandaloneISA({
            ...this.isaForm.value,
            ...this.contactForm.value,
            isaLinkId: id && id !== '' ? id : undefined,
            language: this.languageService.currentLanguage.value.split('-')[0],
            allowRetargeting: this.allowRetargeting,
          })
          .pipe(untilDestroyed(this))
          .subscribe(() => {
            this.submitting = false;
            this.slider.next();
          });
      });
  }
}
